/**
 * @file strategyBuilderSlice.ts
 * @description Redux slice managing active StrategyJSON draft, step navigation, and inline validation.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { StrategyJSON, IndicatorInstance, DirectionalRules } from '@forex-builder/shared-types';
import { validateStrategy, type StrategyValidationIssue } from '@forex-builder/strategy-validator';

export interface StrategyBuilderState {
  strategyId: string | null;
  activeStep: number;
  draft: StrategyJSON;
  isDirty: boolean;
  validationErrors: StrategyValidationIssue[];
  validationWarnings: StrategyValidationIssue[];
}

export const DEFAULT_STRATEGY_TEMPLATE: StrategyJSON = {
  meta: {
    name: 'New Forex Strategy',
    description: 'EMA Crossover strategy template with RSI confirmation and ATR trailing stop.',
    symbol: 'EURUSD',
    timeframe: 'H1',
    direction: 'both',
    initialCapital: 10000,
    leverage: 100,
    startDate: '2023-01-01',
    endDate: '2024-01-01',
    version: 1,
  },
  indicators: [
    {
      id: 'ema_fast_1',
      label: 'EMA 20',
      params: {
        type: 'EMA',
        period: 20,
        priceSource: 'close',
        shift: 0,
      },
    },
    {
      id: 'ema_slow_1',
      label: 'EMA 50',
      params: {
        type: 'EMA',
        period: 50,
        priceSource: 'close',
        shift: 0,
      },
    },
  ],
  entry: {
    long: {
      logic: 'AND',
      conditions: [
        {
          id: 'b1a2c3d4-0001-4000-8000-000000000001',
          left: { kind: 'indicator', indicatorId: 'ema_fast_1' },
          operator: 'crosses_above',
          right: { kind: 'indicator', indicatorId: 'ema_slow_1' },
        },
      ],
    },
    short: {
      logic: 'AND',
      conditions: [
        {
          id: 'b1a2c3d4-0002-4000-8000-000000000002',
          left: { kind: 'indicator', indicatorId: 'ema_fast_1' },
          operator: 'crosses_below',
          right: { kind: 'indicator', indicatorId: 'ema_slow_1' },
        },
      ],
    },
  },
  exit: {
    stopLoss: {
      mode: 'pips',
      value: 30,
    },
    takeProfit: {
      mode: 'rr_ratio',
      value: 2.0,
    },
    trailing: {
      enabled: true,
      mode: 'classic',
      distance: 25,
    },
    breakEven: {
      enabled: false,
      triggerPips: 20,
      lockPips: 2,
    },
    other: {
      exitOnOppositeSignal: true,
      closeAfterNBars: null,
      closeAtSessionEnd: false,
    },
  },
  money: {
    mode: 'fixed_lots',
    value: 0.1,
    maxLot: 5.0,
    martingaleMultiplier: 1,
  },
  management: {
    maxOpenTrades: 1,
    oneTradePerBar: true,
    maxTradesPerDay: 5,
    allowHedging: false,
  },
  filters: {
    sessions: [{ start: '07:00', end: '16:00', tz: 'UTC' }],
    days: [1, 2, 3, 4, 5],
    maxSpreadPoints: 20,
    minBarsBetweenTrades: 0,
  },
  advanced: {
    magicNumber: 100001,
    slippagePoints: 3,
    orderComment: 'ForexBuilder EA',
    retryAttempts: 3,
  },
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ensureUuidConditionIds = (draft: StrategyJSON): StrategyJSON => {
  const sanitize = (conditions: any[]) => {
    if (!Array.isArray(conditions)) return;
    conditions.forEach((c, idx) => {
      if (!c.id || !UUID_REGEX.test(c.id)) {
        c.id = typeof window !== 'undefined' && window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `b1a2c3d4-${idx.toString().padStart(4, '0')}-4000-8000-000000000001`;
      }
    });
  };
  if (draft?.entry?.long?.conditions) sanitize(draft.entry.long.conditions);
  if (draft?.entry?.short?.conditions) sanitize(draft.entry.short.conditions);
  return draft;
};

const runValidation = (draft: StrategyJSON) => {
  ensureUuidConditionIds(draft);
  const result = validateStrategy(draft);
  return {
    errors: result.errors,
    warnings: result.warnings,
  };
};

const initialValidation = runValidation(DEFAULT_STRATEGY_TEMPLATE);

const initialState: StrategyBuilderState = {
  strategyId: null,
  activeStep: 1,
  draft: DEFAULT_STRATEGY_TEMPLATE,
  isDirty: false,
  validationErrors: initialValidation.errors,
  validationWarnings: initialValidation.warnings,
};

export const strategyBuilderSlice = createSlice({
  name: 'strategyBuilder',
  initialState,
  reducers: {
    setStrategyDraft: (state, action: PayloadAction<{ id: string | null; strategyJson: StrategyJSON }>) => {
      state.strategyId = action.payload.id;
      const draft = { ...action.payload.strategyJson };
      if (draft && draft.meta) {
        draft.meta = {
          ...draft.meta,
          startDate: draft.meta.startDate || '2023-01-01',
          endDate: draft.meta.endDate || '2024-01-01',
          initialCapital: draft.meta.initialCapital || 10000,
          leverage: draft.meta.leverage || 100,
        };
      }
      state.draft = draft;
      state.isDirty = false;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    resetStrategyDraft: (state) => {
      state.strategyId = null;
      state.draft = DEFAULT_STRATEGY_TEMPLATE;
      state.activeStep = 1;
      state.isDirty = false;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    setActiveStep: (state, action: PayloadAction<number>) => {
      state.activeStep = Math.min(Math.max(action.payload, 1), 6);
    },
    updateMeta: (state, action: PayloadAction<Partial<StrategyJSON['meta']>>) => {
      state.draft.meta = { ...state.draft.meta, ...action.payload };
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    addIndicator: (state, action: PayloadAction<IndicatorInstance>) => {
      state.draft.indicators.push(action.payload);
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    updateIndicator: (state, action: PayloadAction<{ id: string; updated: IndicatorInstance }>) => {
      const idx = state.draft.indicators.findIndex((i) => i.id === action.payload.id);
      if (idx !== -1) {
        state.draft.indicators[idx] = action.payload.updated;
        state.isDirty = true;
        const v = runValidation(state.draft);
        state.validationErrors = v.errors;
        state.validationWarnings = v.warnings;
      }
    },
    removeIndicator: (state, action: PayloadAction<string>) => {
      state.draft.indicators = state.draft.indicators.filter((i) => i.id !== action.payload);
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    updateDirectionalRules: (
      state,
      action: PayloadAction<{ direction: 'long' | 'short'; rules: DirectionalRules }>,
    ) => {
      state.draft.entry[action.payload.direction] = action.payload.rules;
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    updateExitConfig: (state, action: PayloadAction<Partial<StrategyJSON['exit']>>) => {
      state.draft.exit = { ...state.draft.exit, ...action.payload };
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    updateMoneyConfig: (state, action: PayloadAction<Partial<StrategyJSON['money']>>) => {
      state.draft.money = { ...state.draft.money, ...action.payload };
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    updateFiltersConfig: (state, action: PayloadAction<Partial<StrategyJSON['filters']>>) => {
      state.draft.filters = { ...state.draft.filters, ...action.payload };
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    updateAdvancedConfig: (state, action: PayloadAction<Partial<StrategyJSON['advanced']>>) => {
      state.draft.advanced = { ...state.draft.advanced, ...action.payload };
      state.isDirty = true;
      const v = runValidation(state.draft);
      state.validationErrors = v.errors;
      state.validationWarnings = v.warnings;
    },
    markSaved: (state, action: PayloadAction<string>) => {
      state.strategyId = action.payload;
      state.isDirty = false;
    },
  },
});

export const {
  setStrategyDraft,
  resetStrategyDraft,
  setActiveStep,
  updateMeta,
  addIndicator,
  updateIndicator,
  removeIndicator,
  updateDirectionalRules,
  updateExitConfig,
  updateMoneyConfig,
  updateFiltersConfig,
  updateAdvancedConfig,
  markSaved,
} = strategyBuilderSlice.actions;

export default strategyBuilderSlice.reducer;
